import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import RecordRTC from "recordrtc";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Navbar from "./Navbar";
import {API_URL,FAST_API_URL} from "../api"


const RECORD_SECONDS = 30;

export default function RPPGWebcam() {
  const webcamRef = useRef(null);
  const recorderRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(RECORD_SECONDS);
  const [videoBlob, setVideoBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [vitals, setVitals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waveGraphData, setWaveGraphData] = useState([]);
  


    /* ---------------- SPEECH ---------------- */
    const speak = (msg) => {
      try {
        const speech = new SpeechSynthesisUtterance(msg);
        speech.lang = "en-US";
        window.speechSynthesis.speak(speech);
      } catch (e) {}
    };

  /* ---------------- HEALTH STATUS HELPERS ---------------- */



  const getSDNNStatus = (v) => {
    if (v < 30) return { label: "Low", active: "Low", color: "#fbbc05" };
    if (v <= 96) return { label: "Healthy", active: "Healthy", color: "#34a853" };
    return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
  };

  const getRMSSDStatus = (v) => {
    if (v < 20) return { label: "Low", active: "Low", color: "#fbbc05" };
    if (v <= 90) return { label: "Healthy", active: "Healthy", color: "#34a853" };
    return { label: "Optimal", active: "Optimal", color: "#0f9d58" };
  };

  const getLFHFStatus = (v) => {
    if (v < 1) return { label: "Optimal", active: "Relaxed", color: "#0f9d58" };
    if (v <= 2) return { label: "Healthy", active: "Balanced", color: "#34a853" };
    return { label: "High Stress", active: "Stressed", color: "#ea4335" };
  };

  /* ---------------- RECORDING ---------------- */

  const startRecording = () => {
    const stream = webcamRef.current?.stream;
    if (!stream) return alert("Webcam not available");

    speak("Recording started. Please look at the camera and stay still.");

    recorderRef.current = new RecordRTC(stream, {
      type: "video",
      mimeType: "video/mp4",
      bitsPerSecond: 3000000,
    });

    recorderRef.current.startRecording();
    setIsRecording(true);

    let t = RECORD_SECONDS;
    setTimer(t);

    const interval = setInterval(() => {
      t -= 1;

      setTimer(t);       
      if (t === 60) speak("Half time completed. Keep looking at the camera."); 
      if (t === 5) speak("Recording is about to finish. Please do not move.");
      if (t <= 0) {
        clearInterval(interval);
        stopRecording();
      }
    }, 1000);
  };

  const stopRecording = () => {
    recorderRef.current.stopRecording(() => {
      const blob = recorderRef.current.getBlob();
      setVideoBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setIsRecording(false);
      speak("Recording complete. Review the video and submit.");
    });
  };

  /* ---------------- SUBMIT ---------------- */


  const handleSubmit = async () => {
    if (!videoBlob) return;
    setLoading(true);

    speak("Uploading video. Please wait.");

    const formData = new FormData();
    formData.append("file", videoBlob, "vitals.mp4");

    try {
      const res = await fetch(
        `${FAST_API_URL}/api/vitals/video`,
        { method: "POST", body: formData }
      );

      const data = await res.json();

      setWaveGraphData(
        data.waveform.map((v, i) => ({ time: i, value: v }))
      );

      setVitals({
        heartRate: Math.round(data.heart_rate_bpm_fft || 0),
        spo2: Math.round(data?.spo2?.spo2 || 0),
      
        // HRV
        SDNN: data?.hrv_time?.SDNN_ms || 0,
        RMSSD: data?.hrv_time?.RMSSD_ms || 0,
        LFHF: data?.hrv_freq?.["LF/HF"] || 0,
      
        // ✅ Blood Pressure (safe parsing)
        sbp: Math.round(
          data?.bp_info?.sbp ?? data?.sbp ?? 0
        ),
        dbp: Math.round(
          data?.bp_info?.dbp ?? data?.dbp ?? 0
        ),
      });
      speak("Vitals processed successfully.");
    } catch {
      alert("Analysis failed");
    }

    setLoading(false);
  };

  const resetAll = () => {
    setVideoBlob(null);
    setPreviewUrl("");
    setVitals(null);
    setWaveGraphData([]);
    setTimer(RECORD_SECONDS);
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="vitals-bg">
      <Navbar />

      <div className="vitals-container">
        <h2 className="vitals-title">🩺 Smart Video Vitals Scanner</h2>

        {!videoBlob && !vitals && (
          <div className="glass-card">
            <Webcam ref={webcamRef} audio={false} className="video-preview" />
            {!isRecording ? (
              <button className="btn-primary" onClick={startRecording}>
                ▶ Start Scan
              </button>
            ) : (
              <div className="record-timer">🔴 {timer}s</div>
            )}
          </div>
        )}

        {videoBlob && !vitals && (
          <div className="glass-card">
            <video src={previewUrl} controls className="video-preview" />
            <button onClick={handleSubmit} className="btn-primary">
              {loading ? "Analyzing..." : "Submit"}
            </button>
            <button onClick={resetAll} className="btn-secondary">
              Record Again
            </button>
          </div>
        )}

        {vitals && (
          <div className="results-grid">
            <div className="glass-card">
              <h3>📊 Vitals Summary</h3>

              <p>❤️ Heart Rate: <b>{vitals.heartRate} BPM</b></p>
              <p>🫁 SpO₂: <b>{vitals.spo2}%</b></p>

              {/* SDNN */}
              {(() => {
                const s = getSDNNStatus(vitals.SDNN);
                return (
                  <div className="health-card">
                    <span className="pill" style={{ background: s.color }}>{s.label}</span>
                    <h2>{vitals.SDNN.toFixed(0)} <span>ms</span></h2>
                    <div className="scale">
                      <span className={s.active === "Low" ? "active" : ""}>Low</span>
                      <span className={s.active === "Healthy" ? "active" : ""}>Healthy</span>
                      <span className={s.active === "Optimal" ? "active" : ""}>Optimal</span>
                    </div>
                    <small>
                      The standard deviation of all NN intervals (time between each heartbeat).
                      Higher values usually indicate that your body is coping better with stress.
                    </small>
                  </div>
                );
              })()}

              {/* RMSSD */}
              {(() => {
                const s = getRMSSDStatus(vitals.RMSSD);
                return (
                  <div className="health-card">
                    <span className="pill" style={{ background: s.color }}>{s.label}</span>
                    <h2>{vitals.RMSSD.toFixed(0)} <span>ms</span></h2>
                    <div className="scale">
                      <span className={s.active === "Low" ? "active" : ""}>Low</span>
                      <span className={s.active === "Healthy" ? "active" : ""}>Healthy</span>
                      <span className={s.active === "Optimal" ? "active" : ""}>Optimal</span>
                    </div>
                    <small>
                      The root mean square of successive intervals between normal heartbeats.
                      Higher values indicate better recovery and a relaxed nervous system.
                    </small>
                  </div>
                );
              })()}

              {/* LF/HF */}
              {(() => {
                const s = getLFHFStatus(vitals.LFHF);
                return (
                  <div className="health-card">
                    <span className="pill" style={{ background: s.color }}>{s.label}</span>
                    <h2>{vitals.LFHF.toFixed(2)}</h2>
                    <div className="scale">
                      <span className={s.active === "Relaxed" ? "active" : ""}>Relaxed</span>
                      <span className={s.active === "Balanced" ? "active" : ""}>Balanced</span>
                      <span className={s.active === "Stressed" ? "active" : ""}>Stressed</span>
                    </div>
                    <small>
                      The LF/HF ratio reflects the balance between stress (fight-or-flight)
                      and relaxation (rest-and-digest).
                    </small>
                  </div>
                );
              })()}

{(() => {
  return (
  <div className="health-card">
  <span
    className="pill"
    style={{
      background:
        vitals.sbp < 120 && vitals.dbp < 80
          ? "#34a853"
          : vitals.sbp < 140
          ? "#fbbc05"
          : "#ea4335",
    }}
  >
    Blood Pressure
  </span>

  <h2>
    {vitals.sbp || "--"} / {vitals.dbp || "--"}
    <span> mmHg</span>
  </h2>

  <div className="scale">
    <span
      className={
        vitals.sbp < 120 && vitals.dbp < 80 ? "active" : ""
      }
    >
      Normal
    </span>
    <span
      className={
        vitals.sbp >= 120 && vitals.sbp < 140 ? "active" : ""
      }
    >
      Elevated
    </span>
    <span
      className={vitals.sbp >= 140 ? "active" : ""}
    >
      High
    </span>
  </div>

  <small>
    Camera-based blood pressure is an estimate. For medical decisions,
    always confirm with a cuff-based device.
  </small>
</div>

)})()}



              <button onClick={resetAll} className="btn-primary">
                🔁 Scan Again
              </button>
            </div>

            <div className="glass-card">
              <h3>💓 Heartbeat Waveform</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={waveGraphData}>
                  <Line dataKey="value" stroke="#ff004c" dot={false} />
                  <XAxis hide />
                  <YAxis hide />
                  <Tooltip />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
